import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { searchProject } from '../lib/api';
import {
  SEARCH_MIN_CHARS,
  SEARCH_MAX_CHARS,
  SEARCH_DEBOUNCE_MS,
  KIND_LABEL,
  hitHref,
  groupHits,
  flattenGroups,
} from '../lib/searchView';

/* NAV-FE-5 — the shell's search by number (docs/design/search.md §5; mockup
   prototipo-shell-v12.html:34-40, :192, :537-543). Two characters, a pause,
   ONE read; the hits fall grouped by family and each row opens its record.
   Who sees what is the server's: this box shows what it was served, nothing
   more and nothing less. It never reads on mount — only on input. */

// The field a `/` must not steal from.
const TYPING_TARGET = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';

const ProjectSearch = ({ projectId }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  // The question each answer belongs to: an answer to an older one is dropped.
  const seqRef = useRef(0);
  const [value, setValue] = useState('');
  const [hits, setHits] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const reset = () => {
    setValue('');
    setHits(null);
    setTruncated(false);
    setError(false);
    setOpen(false);
    setActive(-1);
  };

  // Another project → nothing of the last one stays (reset while rendering, so
  // the read below never sees the new project with the old term).
  const [forProject, setForProject] = useState(projectId);
  if (forProject !== projectId) {
    setForProject(projectId);
    reset();
  }

  const q = value.trim();

  useEffect(() => {
    seqRef.current += 1;
    if (!projectId || q.length < SEARCH_MIN_CHARS) return undefined;
    const seq = seqRef.current;
    const timer = setTimeout(() => {
      searchProject(projectId, q)
        .then((res) => {
          if (seq !== seqRef.current) return;
          const data = res?.data || {};
          setHits(Array.isArray(data.hits) ? data.hits : []);
          setTruncated(!!data.truncated);
          setError(false);
          setActive(-1);
          setOpen(true);
        })
        .catch(() => {
          if (seq !== seqRef.current) return;
          setHits([]);
          setTruncated(false);
          setError(true);
          setActive(-1);
          setOpen(true);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [projectId, q]);

  // A row is drawn only when it has a door; the keyboard walks the same rows.
  const groups = useMemo(
    () => (hits ? groupHits(hits.filter((h) => hitHref(h, projectId))) : []),
    [hits, projectId],
  );
  const flat = useMemo(() => flattenGroups(groups), [groups]);
  const showResults = open && hits !== null;

  // `/` focuses the box (mockup :542) — unless the reader is typing elsewhere
  // or a dialog is open.
  useEffect(() => {
    if (!projectId) return undefined;
    const onKey = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = document.activeElement;
      if (el && el.matches && el.matches(TYPING_TARGET)) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      e.preventDefault();
      if (inputRef.current) inputRef.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [projectId]);

  // A click outside closes the list (and keeps the term).
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const openHit = (hit) => {
    const href = hit ? hitHref(hit, projectId) : null;
    if (!href) return;
    reset();
    if (inputRef.current) inputRef.current.blur();
    navigate(href);
  };

  const onChange = (e) => {
    const next = e.target.value;
    setValue(next);
    if (next.trim().length < SEARCH_MIN_CHARS) {
      setHits(null);
      setTruncated(false);
      setError(false);
      setOpen(false);
      setActive(-1);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!showResults || flat.length === 0) return;
      e.preventDefault();
      const n = flat.length;
      if (e.key === 'ArrowDown') setActive((i) => (i < 0 ? 0 : (i + 1) % n));
      else setActive((i) => (i < 0 ? n - 1 : (i - 1 + n) % n));
    } else if (e.key === 'Enter') {
      if (!showResults || flat.length === 0) return;
      e.preventDefault();
      openHit(flat[active] || flat[0]);
    } else if (e.key === 'Escape') {
      if (showResults) {
        setOpen(false);
        setActive(-1);
      } else {
        reset();
        if (inputRef.current) inputRef.current.blur();
      }
    }
  };

  let index = -1;
  return (
    <div
      ref={boxRef}
      className="relative hidden md:flex flex-1 items-center gap-2 max-w-[460px] bg-[#0b1222] border border-[#334155] rounded-lg px-3 py-[6px] text-[#8b95a7] text-[13px]"
      data-testid="shell-search-box"
    >
      <Search className="h-4 w-4 text-[#64748b] shrink-0" />
      <input
        ref={inputRef}
        data-testid="shell-search"
        type="text"
        autoComplete="off"
        spellCheck={false}
        maxLength={SEARCH_MAX_CHARS}
        placeholder="Search by number — asset, ITC, NCR, RFI, test request"
        aria-label="Search by number"
        role="combobox"
        aria-expanded={showResults}
        aria-controls="shell-search-results"
        aria-autocomplete="list"
        aria-activedescendant={showResults && active >= 0 ? `shell-search-option-${active}` : undefined}
        disabled={!projectId}
        title={projectId ? undefined : 'Select a project first'}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={() => { if (hits !== null) setOpen(true); }}
        className="flex-1 min-w-0 bg-transparent border-0 outline-none text-white text-[13px] placeholder:text-[#8b95a7]"
      />
      <kbd
        data-testid="shell-search-kbd"
        className="hidden lg:inline text-[10px] border border-[#334155] rounded px-[5px] py-px text-[#64748b]"
      >
        /
      </kbd>
      {showResults && (
        <div
          data-testid="shell-search-results"
          id="shell-search-results"
          role="listbox"
          className="absolute left-0 right-0 top-[38px] bg-[#0b1222] border border-[#334155] rounded-[10px] p-[6px] z-50 shadow-[0_12px_30px_rgba(0,0,0,.6)] max-h-[60vh] overflow-y-auto"
        >
          {groups.map((g) => (
            <div key={g.kind} role="group" aria-label={KIND_LABEL[g.kind]}>
              <div
                data-testid={`shell-search-group-${g.kind}`}
                className="px-[10px] pt-[6px] pb-[2px] text-[10.5px] uppercase tracking-[.06em] text-[#64748b]"
              >
                {KIND_LABEL[g.kind]}
              </div>
              {g.hits.map((hit) => {
                index += 1;
                const isActive = index === active;
                return (
                  <button
                    key={`${hit.kind}-${hit.id}`}
                    id={`shell-search-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    data-testid={`shell-search-hit-${hit.kind}-${hit.id}`}
                    data-active={isActive ? 'true' : undefined}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => openHit(hit)}
                    className={`w-full flex items-baseline gap-[10px] px-[10px] py-[7px] rounded-[7px] text-[13px] text-left hover:bg-[#111b31]${isActive ? ' bg-[#111b31]' : ''}`}
                  >
                    <span className="font-mono text-[#f97316]">{hit.number}</span>
                    <span className="text-[#8b95a7] truncate min-w-0 flex-1">{hit.title}</span>
                    {hit.asset && hit.kind !== 'asset' && (
                      <span className="font-mono text-[11.5px] text-[#64748b] shrink-0">{hit.asset.asset_number}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
          {error && (
            <div data-testid="shell-search-error" className="px-[10px] py-[7px] text-[13px] text-[#8b95a7]">
              Search unavailable
            </div>
          )}
          {!error && flat.length === 0 && (
            <div data-testid="shell-search-empty" className="px-[10px] py-[7px] text-[13px] text-[#8b95a7]">
              No match
            </div>
          )}
          {!error && truncated && (
            <div data-testid="shell-search-truncated" className="px-[10px] pt-[6px] pb-[4px] text-[12px] text-[#64748b]">
              Showing the first 20 — type more of the number
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectSearch;
